import Email, { MailsListId } from "@/domain/entities/emails/Email";
import { isSameCopy } from "@/lib/mailIdentity";
import { queryKeys } from "@/lib/queryKeys";
import mailStore, { setOpenedMail } from "@/stores/mailStore";
import {
    BulkMailAction,
    SmartLabel,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { useCallback } from "react";
import {
    listIsEmptyWithMore,
    MailsListCache,
    removeMailsFromList,
    restoreMailsInList,
    updateMailsInList,
} from "./mails/mailsListCache";
import useErrorToast from "./utils/useErrorToast";
import useSuccessToast from "./utils/useSuccessToast";

type CacheMutation = (list: MailsListCache) => MailsListCache;

type RunMailActionParams = {
    listId: MailsListId;
    serverAction: () => Promise<unknown>;
    mutateCache: CacheMutation;
    successToastKey?: string;
    // Bulk only: lets a partial server failure be reverted per-mail.
    affectedMails?: Email[];
};

// BulkActionResult.failed is in uniqueMailvistaId space (SDK ≥ 0.9.4), and partial failures don't throw.
const failedUniqueMailvistaIds = (result: unknown): string[] =>
    result !== null &&
    typeof result === "object" &&
    Array.isArray((result as { failed?: unknown }).failed)
        ? (result as { failed: unknown[] }).failed.filter(
              (id): id is string => typeof id === "string",
          )
        : [];

const isSmartLabelList = (listId: MailsListId): boolean =>
    Object.values(SmartLabel).includes(listId as SmartLabel);

// On a smart-label list, (un)processing moves the mail out of the list;
// elsewhere it just flips the badge.
const processedCacheMutation = (
    listId: MailsListId,
    items: Email[],
    isProcessed: boolean,
): CacheMutation =>
    isSmartLabelList(listId)
        ? (list) => removeMailsFromList(list, items)
        : (list) =>
              updateMailsInList(list, items, { isUserProcessed: isProcessed });

// Same as processing, but "Traités" keeps its mails when focus is toggled.
const focusCacheMutation = (
    listId: MailsListId,
    items: Email[],
    isFocus: boolean,
): CacheMutation =>
    isSmartLabelList(listId) && listId !== SmartLabel.Traites
        ? (list) => removeMailsFromList(list, items)
        : (list) => updateMailsInList(list, items, { isFocus });

const useMailActions = () => {
    const { mailManager } = useSdk();
    const queryClient = useQueryClient();
    const showErrorToast = useErrorToast();
    const showSuccessToast = useSuccessToast();
    const openedMail = useStore(mailStore, (state) => state.openedMail);

    // PR #943: when an action empties the loaded page but more mails exist
    // server-side, reset pagination to reload the next batch from offset 0.
    const reloadIfListEmptied = useCallback(
        async (listId: MailsListId): Promise<void> => {
            const list = queryClient.getQueryData<MailsListCache>(
                queryKeys.mails.list(listId),
            );
            if (list && listIsEmptyWithMore(list)) {
                await queryClient.resetQueries({
                    queryKey: queryKeys.mails.list(listId),
                });
            }
        },
        [queryClient],
    );

    const reconcileOpenedMail = useCallback(
        ({
            previous,
            next,
            listId,
        }: {
            previous: MailsListCache;
            next: MailsListCache;
            listId: MailsListId;
        }): void => {
            if (!openedMail) return;

            const flatNext = next.pages.flatMap((page) => page.data);
            const stillVisible = flatNext.find((mail) =>
                isSameCopy(mail, openedMail),
            );
            if (stillVisible) {
                setOpenedMail(stillVisible, { openIntent: false });

                return;
            }

            // The opened mail left the list: open the one that takes its place.
            const flatPrevious = previous.pages.flatMap((page) => page.data);
            const removedIndex = flatPrevious.findIndex((mail) =>
                isSameCopy(mail, openedMail),
            );
            if (removedIndex === -1) return;

            let nextMail: Email | undefined;
            if (flatNext.length > 0) {
                // The mail now at the removed slot, otherwise the last one.
                nextMail =
                    flatNext[removedIndex] || flatNext[flatNext.length - 1];
                if (nextMail && !nextMail.isRead) {
                    // Opening a mail marks it read: reflect it in the cache.
                    queryClient.setQueryData(
                        queryKeys.mails.list(listId),
                        updateMailsInList(next, [nextMail], { isRead: true }),
                    );
                    nextMail = new Email({ ...nextMail, isRead: true });
                }
            }
            setOpenedMail(nextMail, { openIntent: false });
        },
        [openedMail, queryClient],
    );

    // Shared orchestrator: optimistic cache update → reconcile opened mail →
    // SDK call, with rollback on error and pagination reload on success.
    const runMailAction = useCallback(
        async ({
            listId,
            serverAction,
            mutateCache,
            successToastKey,
            affectedMails,
        }: RunMailActionParams): Promise<boolean> => {
            const previous = queryClient.getQueryData<MailsListCache>(
                queryKeys.mails.list(listId),
            );
            // No cached list (never fetched or garbage-collected): skip the
            // optimistic update but still run the server action.
            const next = previous ? mutateCache(previous) : undefined;

            if (previous && next) {
                queryClient.setQueryData(queryKeys.mails.list(listId), next);
                reconcileOpenedMail({ previous, next, listId });
            }

            try {
                const result = await serverAction();

                const failedIds = new Set(failedUniqueMailvistaIds(result));
                const failedMails = affectedMails?.filter((mail) =>
                    failedIds.has(mail.uniqueMailvistaId),
                );

                if (failedMails?.length) {
                    if (next) {
                        queryClient.setQueryData<MailsListCache>(
                            queryKeys.mails.list(listId),
                            (current) =>
                                restoreMailsInList(
                                    current ?? next,
                                    failedMails,
                                ),
                        );
                    }
                    showErrorToast({
                        titleTranslationKey: "errors.bulkActionsPartial",
                    });
                    await reloadIfListEmptied(listId);

                    return false;
                }

                if (successToastKey) {
                    showSuccessToast({ titleTranslationKey: successToastKey });
                }
                await reloadIfListEmptied(listId);

                return true;
            } catch (error) {
                console.error(error);
                showErrorToast({ titleTranslationKey: "errors.emailsActions" });
                if (previous) {
                    // Bulk reverts only its mails (live cache) so concurrent actions survive; single rolls back fully.
                    queryClient.setQueryData<MailsListCache>(
                        queryKeys.mails.list(listId),
                        (current) =>
                            affectedMails
                                ? restoreMailsInList(
                                      current ?? previous,
                                      affectedMails,
                                  )
                                : previous,
                    );
                    if (openedMail) {
                        setOpenedMail(openedMail, { openIntent: false });
                    }
                }

                return false;
            }
        },
        [
            openedMail,
            queryClient,
            reconcileOpenedMail,
            reloadIfListEmptied,
            showErrorToast,
            showSuccessToast,
        ],
    );

    const runBulkAction = useCallback(
        (action: BulkMailAction, mails: Email[]) =>
            mailManager.executeBulkAction(
                action,
                mails.map((mail) => mail.uniqueMailvistaId),
            ),
        [mailManager],
    );

    // ─── Single actions ─────────────────────────────────────────────────────
    const setReadStatus = useMutation({
        mutationFn: async ({
            mail,
            listId,
            readStatus,
            successToastKey,
        }: {
            mail: Email;
            listId: MailsListId;
            readStatus: boolean;
            successToastKey?: string;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    mailManager.setReadStatus(
                        mail.uniqueMailvistaId,
                        readStatus,
                    ),
                mutateCache: (list) =>
                    updateMailsInList(list, [mail], { isRead: readStatus }),
                successToastKey,
            }),
    });

    const setProcessStatus = useMutation({
        mutationFn: async ({
            mail,
            listId,
            isUserProcessed,
            successToastKey,
        }: {
            mail: Email;
            listId: MailsListId;
            isUserProcessed: boolean;
            successToastKey?: string;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    mailManager.setMailProcessed(
                        mail.uniqueMailvistaId,
                        isUserProcessed,
                    ),
                mutateCache: processedCacheMutation(
                    listId,
                    [mail],
                    isUserProcessed,
                ),
                successToastKey,
            }),
    });

    const setMailFocus = useMutation({
        mutationFn: async ({
            mail,
            listId,
            isFocus,
            successToastKey,
        }: {
            mail: Email;
            listId: MailsListId;
            isFocus: boolean;
            successToastKey?: string;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    mailManager.setMailFocus(mail.uniqueMailvistaId, isFocus),
                mutateCache: focusCacheMutation(listId, [mail], isFocus),
                successToastKey,
            }),
    });

    const deleteMail = useMutation({
        mutationFn: async ({
            mail,
            listId,
            successToastKey,
        }: {
            mail: Email;
            listId: MailsListId;
            successToastKey?: string;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    mailManager.deleteMail(mail.uniqueMailvistaId),
                mutateCache: (list) => removeMailsFromList(list, [mail]),
                successToastKey,
            }),
    });

    // ─── Bulk actions ───────────────────────────────────────────────────────
    const setReadStatusBulk = useMutation({
        mutationFn: async ({
            mails,
            listId,
            readStatus,
        }: {
            mails: Email[];
            listId: MailsListId;
            readStatus: boolean;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    runBulkAction(
                        readStatus
                            ? BulkMailAction.MARK_AS_READ
                            : BulkMailAction.MARK_AS_UNREAD,
                        mails,
                    ),
                mutateCache: (list) =>
                    updateMailsInList(list, mails, { isRead: readStatus }),
                successToastKey: readStatus
                    ? "success.bulkActions.markedAsRead"
                    : "success.bulkActions.markedAsUnread",
                affectedMails: mails,
            }),
    });

    const setProcessStatusBulk = useMutation({
        mutationFn: async ({
            mails,
            listId,
            isProcessed,
        }: {
            mails: Email[];
            listId: MailsListId;
            isProcessed: boolean;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    runBulkAction(
                        isProcessed
                            ? BulkMailAction.MARK_AS_USER_PROCESSED
                            : BulkMailAction.MARK_AS_USER_UNPROCESSED,
                        mails,
                    ),
                mutateCache: processedCacheMutation(listId, mails, isProcessed),
                successToastKey: isProcessed
                    ? "success.bulkActions.markedAsProcessed"
                    : "success.bulkActions.markedAsUnprocessed",
                affectedMails: mails,
            }),
    });

    const setMailFocusBulk = useMutation({
        mutationFn: async ({
            mails,
            listId,
            isFocus,
        }: {
            mails: Email[];
            listId: MailsListId;
            isFocus: boolean;
        }) =>
            runMailAction({
                listId,
                serverAction: () =>
                    runBulkAction(
                        isFocus
                            ? BulkMailAction.MARK_AS_FOCUS
                            : BulkMailAction.MARK_AS_UNFOCUS,
                        mails,
                    ),
                mutateCache: focusCacheMutation(listId, mails, isFocus),
                successToastKey: isFocus
                    ? "success.bulkActions.markedAsFocus"
                    : "success.bulkActions.markedAsUnfocus",
                affectedMails: mails,
            }),
    });

    const deleteMailsBulk = useMutation({
        mutationFn: async ({
            mails,
            listId,
        }: {
            mails: Email[];
            listId: MailsListId;
        }) =>
            runMailAction({
                listId,
                serverAction: () => runBulkAction(BulkMailAction.DELETE, mails),
                mutateCache: (list) => removeMailsFromList(list, mails),
                successToastKey: "success.bulkActions.deleted",
                affectedMails: mails,
            }),
    });

    return {
        setReadStatus,
        setProcessStatus,
        setMailFocus,
        deleteMail,
        setReadStatusBulk,
        setProcessStatusBulk,
        setMailFocusBulk,
        deleteMailsBulk,
    };
};

export default useMailActions;
